import NotificationItem from "@/components/Notifications/NotificationItem";
import { useNotifications } from "@/contexts/NotificationsContext";
import { useEffect, useState } from "react";
import { Notification } from "@/types/globalTypes";
import axios from "axios";
import { useSelector } from "react-redux";
import ErrorMessage from "@/components/ErrorMessage";
import { Button } from "@/components/ui/button";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";

import { FaAnglesLeft, FaAnglesRight } from "react-icons/fa6";
import { Skeleton } from "@/components/ui/skeleton";
import { useTranslation } from "react-i18next";

export default function NotificationsPage() {
  const { t, i18n } = useTranslation();
  const { markAsRead, needNotificationsRefetch } = useNotifications();
  const [allNotifcations, setAllNotifications] = useState<Notification[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const BASE_URL = import.meta.env.VITE_BASE_URL;
  const userToken = useSelector(
    (state: { auth: { token: string } }) => state.auth.token,
  );

  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const statusOptions = [
    { label: t("notifications.all") as string, value: "all" }, // Changed value from "" to "all"
    { label: t("notifications.read") as string, value: "read" },
    { label: t("notifications.unread") as string, value: "unread" },
  ];

  const [status, setStatus] = useState<"read" | "unread" | "all">("all"); // Updated type and default value

  useEffect(() => {
    if (!userToken) return

    setIsLoading(true);
    setError(null);

    const fetchNotifications = async () => {
      let url = `${BASE_URL}/api/dashboard/notifications?page=${page}`;
      if (status !== "all") {
        // Only append status if it's not "all"
        url += `&status=${status}`;
      }

      try {
        const response = await axios.get(url, {
          headers: {
            Authorization: userToken,
            "Content-Type": "application/json",
          },
        });

        if (response.data.success) {
          setAllNotifications(response.data.notifications);
          setTotalPages(response.data.pagination.totalPages || 1);
        } else {
          setError(
            response.data.error ||
            t("notifications.error")
          );
          setAllNotifications([]);
        }
      } catch (error) {
        console.error("Error fetching notifications:", error);
        setError(
          t("notifications.fetchFailed") as string,
        );
      } finally {
        setIsLoading(false);
      }
    };

    fetchNotifications();
  }, [needNotificationsRefetch, status, page, BASE_URL, userToken, t]);

  if (isLoading) return (
    <div className="flex-col flex gap-2 w-full mt-[5rem]">
      <Skeleton className=" dark:bg-slate-600 dark:bg-opacity-35 w-full  h-[100px]" />
      <Skeleton className=" dark:bg-slate-600 dark:bg-opacity-35 w-full  h-[100px]" />
      <Skeleton className=" dark:bg-slate-600 dark:bg-opacity-35 w-full  h-[100px]" />
      <Skeleton className=" dark:bg-slate-600 dark:bg-opacity-35 w-full    h-[100px]" />
      <Skeleton className=" dark:bg-slate-600 dark:bg-opacity-35 w-full    h-[100px]" />
    </div>
  )
  if (error) return <ErrorMessage msg={error} />;

  return (
    <div>
      <div className="mb-10 flex flex-wrap items-center justify-between gap-x-5 gap-y-4">
        <h1 className="text-3xl font-semibold">
          {t("notifications.title")}
        </h1>

        <div className="flex flex-wrap-reverse gap-3">
          {/* Filter Notifications */}
          <Select
            value={status}
            onValueChange={(value) =>
              setStatus(value as "all" | "read" | "unread")
            }
          >
            <SelectTrigger className="w-[110px] bg-sidebar text-skyblue dark:bg-sidebar dark:text-skyblue">
              <SelectValue className="" />
            </SelectTrigger>
            <SelectContent className="border-none bg-sidebar dark:bg-sidebar">
              {statusOptions.map((option) => (
                <SelectItem
                  key={option.value}
                  value={option.value}
                  className="bg-sidebar text-skyblue focus:bg-skyblue focus:text-sidebar active:bg-skyblue active:text-sidebar dark:bg-sidebar dark:text-skyblue focus:dark:bg-skyblue focus:dark:text-sidebar"
                >
                  {option.label}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>

          <button
            onClick={() => markAsRead()}
            className="rounded-md bg-sidebar text-sm px-3 py-1 text-skyblue"
          >
            {t("notifications.markAllAsRead")}
          </button>
        </div>
      </div>

      {/* Notification List */}
      <div className="thin-scrollbar flex h-[57dvh] md:h-[60dvh] lg:h-[65dvh] flex-col gap-2 overflow-y-scroll rounded-sm">
        {allNotifcations.length === 0 && (
          <ErrorMessage
            msg={t("notifications.noNotifications") as string}
          />
        )}
        {allNotifcations.map((notification) => (
          <NotificationItem
            key={notification.id}
            notification={notification}
          />
        ))}
      </div>

      {/* Pagination Controls */}
      <div className="mt-5 flex justify-center gap-3">
        <Button
          className="bg-gray-400 dark:bg-gray-400"
          disabled={page <= 1}
          onClick={() => setPage((prev) => Math.max(prev - 1, 1))}
        >
          {i18n.language === "ar" ?  <FaAnglesRight /> :<FaAnglesLeft />}
        </Button>

        <div className="flex items-center gap-2">
          {/* Always show the first page */}
          <Button
            key={1}
            variant={1 === page ? "outline" : "default"}
            onClick={() => setPage(1)}
            className={`text-black hover:bg-white ${1 === page
              ? "bg-sidebar text-skyblue hover:bg-sidebar hover:text-skyblue"
              : "bg-white text-black"
              }`}
          >
            1
          </Button>

          {/* Show dots if the current page is far from the start */}
          {page > 3 && <span className="px-2 hidden sm:inline-block">...</span>}

          {/* Show buttons around the current page */}
          {Array.from({ length: totalPages }, (_, index) => index + 1)
            .filter(
              (pageNum) =>
                pageNum > 1 &&
                pageNum < totalPages &&
                Math.abs(pageNum - page) <= 1
            )
            .map((pageNum) => (
              <Button
                key={pageNum}
                variant={pageNum === page ? "outline" : "default"}
                onClick={() => setPage(pageNum)}
                className={`text-black hover:bg-white ${pageNum === page
                  ? "bg-sidebar text-skyblue hover:bg-sidebar hover:text-skyblue"
                  : "bg-white text-black"
                  }`}
              >
                {pageNum}
              </Button>
            ))}

          {/* Show dots if the current page is far from the end */}
          {page < totalPages - 2 && <span className="px-2 hidden sm:inline-block">...</span>}

          {/* Always show the last page */}
          {totalPages > 1 && (
            <Button
              key={totalPages}
              variant={totalPages === page ? "outline" : "default"}
              onClick={() => setPage(totalPages)}
              className={`text-black hover:bg-white ${totalPages === page
                ? "bg-sidebar text-skyblue hover:bg-sidebar hover:text-skyblue"
                : "bg-white text-black"
                }`}
            >
              {totalPages}
            </Button>
          )}
        </div>

        <Button
          className="bg-gray-400 dark:bg-gray-400"
          disabled={page >= totalPages}
          onClick={() => setPage((prev) => prev + 1)}
        >
          {i18n.language === "ar" ? <FaAnglesLeft /> : <FaAnglesRight />}
          </Button>
      </div>
    </div>
  );
}
